import { Toaster as Sonner } from 'sonner';

import { cn } from '~/lib/utils';

/**
 * Sonner toast config for hhOS. Import `toast` directly from `sonner` at call
 * sites; this file owns the Toaster chrome and the type palette.
 *
 * Type palette — the left-border stripe is the only type signal, four colors:
 *   toast.success(...)  → green   action completed
 *   toast.error(...)    → red     action failed
 *   toast.warning(...)  → amber   non-blocking issue needing attention
 *   toast(...)          → gray    neutral (also toast.loading / toast.info)
 *
 * Voice — terse, no punctuation, no "successfully", no exclamation marks:
 *   success:  `Visit saved`, `Tag added`, `Appointment canceled`
 *   error:    `Failed to {verb} {thing}` — never "Something went wrong"
 *   copy:     `{thing} copied` — not "copied to clipboard"
 *
 * Rules:
 *   - Never set a custom `duration` — Sonner defaults are correct. The only
 *     exception is `duration: Infinity` for a persistent banner (see below).
 *   - Never toast for navigation or page transitions.
 *   - For in-progress async ops use `toast.loading()` and pair with
 *     `toast.dismiss(id)` (or reuse the same `{ id }`) on completion.
 *   - `action` is for undo / follow-up (e.g. `{ label: 'Undo', onClick }`).
 *   - `description` is for important context only, used sparingly.
 *
 * Persistent toasts — when a toast needs to stay on screen indefinitely,
 * prefer a built-in type (`toast.warning`, `toast.info`, etc.) with
 * `{ duration: Infinity, closeButton: true, action: { label, onClick } }`.
 * The Toaster chrome stays consistent and the toast remains compact.
 * Canonical example: `app/components/VersionUpdateBanner.tsx`.
 *
 * Custom-chrome toasts — only when the built-in shape genuinely doesn't fit
 * (e.g. multi-line layout, custom buttons), fall back to `toast.custom()` with
 * `unstyled: true` and build the chrome yourself. This is a heavier pattern
 * and visually departs from the rest of the toast palette — use sparingly.
 */

type ToasterProps = React.ComponentProps<typeof Sonner>;

// Toast buttons are 24px tall. On touch they grow to a 44px finger target through
// a transparent pseudo-element, so the toast looks the same (the same pattern as
// `touchHitArea` in `messaging/hoverControl.ts`). Undo is the control this
// matters for: it is the one thing on a toast you have seconds to hit
// (HHOS-2399).
const TOUCH_BUTTON_HIT_AREA =
  'group-[.toast]:touch:relative group-[.toast]:touch:before:absolute group-[.toast]:touch:before:-inset-x-2 group-[.toast]:touch:before:-inset-y-2.5 group-[.toast]:touch:before:content-[""]';

const Toaster = ({ ...props }: ToasterProps) => {
  return (
    <Sonner
      theme="light"
      className="toaster group"
      visibleToasts={5}
      offset={24}
      gap={10}
      closeButton
      toastOptions={{
        classNames: {
          // Base chrome for every toast — gray left stripe is the default
          // when no type is set (plain `toast()`, `toast.loading`, `toast.info`).
          toast:
            'group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-xl group-[.toaster]:border-l-4 group-[.toaster]:py-3 group-[.toaster]:px-4 group-[.toaster]:!overflow-visible',
          title:
            'group-[.toast]:font-semibold group-[.toast]:text-[13px] group-[.toast]:leading-tight',
          description:
            'group-[.toast]:text-muted-foreground group-[.toast]:text-xs group-[.toast]:leading-snug',
          actionButton: cn(
            TOUCH_BUTTON_HIT_AREA,
            'group-[.toast]:bg-brand-dark group-[.toast]:text-brand-foreground group-[.toast]:text-xs group-[.toast]:px-3 group-[.toast]:py-1 group-[.toast]:rounded-md group-[.toast]:font-medium group-[.toast]:transition-all group-[.toast]:duration-150 group-[.toast]:shadow-[0_1px_2px_rgba(0,0,0,0.15),inset_0_-1px_0_rgba(0,0,0,0.2)] group-[.toast]:hover:bg-brand-dark-hover group-[.toast]:active:bg-brand-dark-pressed group-[.toast]:active:shadow-[inset_0_2px_4px_rgba(0,0,0,0.3)] group-[.toast]:active:scale-[0.98]'
          ),
          cancelButton: cn(
            TOUCH_BUTTON_HIT_AREA,
            'group-[.toast]:bg-muted group-[.toast]:text-muted-foreground'
          ),
          closeButton:
            'group-[.toast]:!-left-2 group-[.toast]:!right-auto group-[.toast]:!-top-2 group-[.toast]:!transform-none group-[.toast]:!h-5 group-[.toast]:!w-5 group-[.toast]:!rounded-full group-[.toast]:!border group-[.toast]:!border-border group-[.toast]:!bg-background group-[.toast]:!text-muted-foreground group-[.toast]:!shadow-md group-[.toast]:!cursor-pointer group-[.toast]:hover:!bg-muted group-[.toast]:hover:!text-foreground',
          // Green — action completed. `toast.success('Visit saved')`.
          success:
            'group-[.toaster]:text-foreground group-[.toaster]:border-l-green-500 [&_[data-icon]]:text-green-500 [&_[data-icon]>svg]:h-5 [&_[data-icon]>svg]:w-5',
          // Red — action failed. `toast.error('Failed to save visit')`.
          error:
            'group-[.toaster]:text-foreground group-[.toaster]:border-l-red-500 [&_[data-icon]]:text-red-500 [&_[data-icon]>svg]:h-5 [&_[data-icon]>svg]:w-5',
          // Amber — non-blocking issue needing attention. `toast.warning(...)`.
          warning:
            'group-[.toaster]:text-foreground group-[.toaster]:border-l-amber-500 [&_[data-icon]]:text-amber-500 [&_[data-icon]>svg]:h-5 [&_[data-icon]>svg]:w-5',
        },
      }}
      {...props}
    />
  );
};

export { Toaster };
